import type {BookingflowStateMachine} from "../../bookingflowstatemachine";
import {State} from "../state";
import type {calActions, CalendarActions, CalendarStateDependencies, IGetCalendarData} from "./calendarState.types";
import DetailsState from "../detailsState";
import PackageState from "../packageState";
import {calendarActions} from "./calendarState.types";
import BasisTypeState from "../basistypesState";
import type {PackageStateDependencies} from "../packageState/packageState.types";
import type {DetailsStateDependencies} from "../detailsState/detailsState.types";
import {Address, BasisType, Booking, CustomerType, DKK, Goal, Medium, Partner, RRDate, Service, TAVBasisTypes, Time} from "@raskrask-dk/raskrask-utils";
import NotAvailableState from "../notavailableState";

const CALENDAR_STATE = "CALENDAR";
export default class CalendarState extends State<CalendarActions, typeof CALENDAR_STATE> {
  public readonly type = CALENDAR_STATE;
  private _address: Address;
  private _basisType: BasisType;
  private _customerType: CustomerType;
  private _goal: Goal | undefined;
  private _serviceType: Service | undefined;
  private _partner: Partner | undefined;
  private _date: RRDate | undefined;
  private _time: Time | undefined;

  /**
   *
   * @param bookingflowStateMachine
   * @param date can be set in the OnDemandState, which will initialize this state with a set date.
   * The inputs below can all be set on teh basis of a data fetch (rebooking/movebooking)
   * @param partner can be set in the OnDemandState, which'll initiate this state with a set partner.
   * @param serviceType can be set in the OnDemandState, which'll initiate this state with the chosen ServiceType if OnDemand isn't available for that servicetype.
   * @param goal can be set in the OnDemandState, which'll initiate this state with a set Goal if OnDemand isn't avaiulabel for that Goal.
   */
  constructor(bookingflowStateMachine: BookingflowStateMachine, dependencies: CalendarStateDependencies) {
    super(bookingflowStateMachine);
    const {address, basisType, customerType, goal, service, partner} = dependencies;
    this._address = address;
    this._basisType = basisType;
    this._customerType = customerType;
    this._goal = goal;
    this._serviceType = service;
    this._partner = partner;
  }

  /**
   * Creates a Product from whats been chosen and initializes the DetailsState with that Product.
   * @returns DetailsState
   */
  nextState = (): DetailsState => {
    return new DetailsState(this.bookingflowStateMachine, this.detailsDeps);
  };

  private get detailsDeps(): DetailsStateDependencies {
    const dateTime = `${this._date?.toString("yyyy-MM-dd")} ${this._time as Time}:00`;
    const serviceDate = new RRDate(dateTime, "yyyy-MM-dd HH:mm:ss");
    const product = new Booking(this._serviceType as Service, this._address, this._partner as Partner, serviceDate);
    return {
      product: product,
      address: this._address,
      customerType: this._customerType,
      ...(this.bookingflowStateMachine.suggestiveChoices !== "EMPTY" && this.bookingflowStateMachine.suggestiveChoices),
    };
  }

  private getPackageStateDeps(): PackageStateDependencies {
    const {address, basisType, customerType, partner, goal, serviceType} = this.getData();
    return {
      address: address,
      basisType: basisType,
      customerType: customerType,
      goal: goal,
      service: serviceType,
      partner: partner,
    };
  }

  //when a higher order value changes, we need to update the available options for the rest of the actions
  // goal->

  doAction(action: CalendarActions): CalendarState | PackageState | DetailsState | NotAvailableState {
    switch (action.type) {
      case "SET_GOAL":
        this._goal = action.isValid() ? this.lookupGoal(action.data as Goal) : undefined;
        this.selectFromPreviousChoices();
        return this;
      case "SET_SERVICETYPE":
        this._serviceType = action.isValid() ? this.lookupService(action.data as Service) : undefined;
        this.selectFromPreviousChoices();
        return this;
      case "SET_PARTNER":
        this._partner = action.isValid() ? this.lookupPartner(action.data as Partner) : undefined;
        this.selectFromPreviousChoices();
        return this;
      case "SET_DATE":
        this._time = undefined;
        this._date = action.isValid() ? this.lookupDate(action.data as RRDate) : undefined;
        this.selectFromPreviousChoices();
        return this;
      case "SET_TIME":
        this._time = action.isValid() ? this.lookupTime(action.data as Time) : undefined;
        return this;
      case "CHOOSE_PACKAGE":
        return new PackageState(this.bookingflowStateMachine, this.getPackageStateDeps());
      case "CHOOSE_NO_AVAILABLE":
        return new NotAvailableState(this.bookingflowStateMachine);

      case "NEXT_STATE":
        return this.nextState();
      default:
        throw new Error("These are not the droids you are looking for.");
    }
  }

  public getActions = (): calActions[] => calendarActions.filter((action) => new action(this).isDoable());

  /**
   *
   * @param action - The action that is given
   *
   */
  public unset = (action: CalendarActions): void => {
    switch (action.type) {
      case "SET_PARTNER" || "SET_DATE":
        this._time = undefined;
        break;
    }
  };

  public get time(): Time | undefined {
    return this._time;
  }
  public get date(): RRDate | undefined {
    return this._date;
  }
  public get serviceType(): Service | undefined {
    return this._serviceType;
  }
  public get partner(): Partner | undefined {
    return this._partner;
  }
  public get goal(): Goal | undefined {
    return this._goal;
  }
  public get customerType(): CustomerType {
    return this._customerType;
  }

  public get orderAddress(): Address {
    return this._address;
  }

  public get basisType(): BasisType {
    return this._basisType;
  }

  /**
   * Maybe this is spaghetti idk, feel free to make suggestions as to how this can be made cleaner/better.
   * @powerleech: This one is problematic, as the user has not chosen servicetype yet.
   * It is only relavant for the 15 minutes.
   */
  public get medium(): Medium | undefined {
    return "InPerson";
    // const is15MinuteChosen = (this._serviceType as Service).duration === 15 ? true : false;
    // return is15MinuteChosen ? "Online" : "InPerson";
  }

  public get shortestDuration(): number {
    const basisTypeData = this.bookingflowStateMachine.stack.filter((state) => state instanceof BasisTypeState)[0].data as TAVBasisTypes;
    const {basisType} = this.bookingflowStateMachine.getData();
    const medium = this.medium;
    const customerType = this.customerType;
    const id = `${basisType?.id}_${medium}_${customerType}`;
    return basisTypeData.junctionAVBasisTypes.byId[id].ShortestDuration;
  }

  public get packageSaveMinAndPriceFrom(): {saveMin: DKK; priceFrom: DKK} | undefined {
    if (this.goal === undefined || this.serviceType === undefined) return undefined;

    if (this.partner !== undefined) {
      return this.partner.getSaveFromAndPriceFrom(this.goal);
    } else {
      return this.goal.cheapestService.cheapestPartner.getSaveFromAndPriceFrom(this.goal);
    }
  }

  public getData(): IGetCalendarData {
    return {
      address: this.orderAddress,
      basisType: this.basisType,
      customerType: this.customerType,
      goal: this.goal,
      serviceType: this.serviceType,
      partner: this.partner,
      date: this.date,
      time: this.time,
      packageSaveMinAndPriceFrom: undefined,
      // packageSaveMinAndPriceFrom: this.packageSaveMinAndPriceFrom,
    };
  }

  selectFromPreviousChoices(): void {
    const goal = this._goal!;
    this._serviceType = this.preSelectedService(goal);
    this._date = this.preSelectedDate(this._serviceType, this._partner);
    this._partner = this.preSelectedPartner(this._serviceType, this._date);
  }

  private preSelectedService = (goal: Goal): Service => {
    const previousService = this.getData().serviceType;
    const defaultService = (): Service => goal.services.find((serviceType) => serviceType.duration.minutes == 60) ?? goal.services[0];
    if (previousService === undefined) return defaultService();
    return goal.services.find((serviceType) => serviceType.duration.toMillis() == previousService.duration.toMillis()) ?? defaultService();
  };

  private preSelectedPartner = (serviceType: Service, date: RRDate | undefined): Partner | undefined => {
    const previousPartner = this.getData().partner;
    const defaultPartner = (): Partner | undefined => undefined;
    if (previousPartner === undefined) return defaultPartner();
    return serviceType.getPartnersBy(date).find((partner) => partner.id == previousPartner.id) ?? defaultPartner();
  };

  private preSelectedDate = (serviceType: Service, partner: Partner | undefined): RRDate | undefined => {
    const previousDate = this.getData().date;
    if (previousDate === undefined) return undefined;
    const defaultDate = (): RRDate | undefined => (serviceType.getDates([previousDate]).length > 0 ? previousDate : undefined);
    return partner?.canServiceBeProvidedOnDate(previousDate, serviceType) ? previousDate : defaultDate();
  };

  private lookupGoal = (lg: Goal | undefined): Goal | undefined => {
    if (lg === undefined) return undefined;
    return this.bookingflowStateMachine.service.schedules.goals.find((g) => g.id === lg.id) || undefined;
  };

  private lookupService = (ls: Service | undefined): Service | undefined => {
    if (ls === undefined) return undefined;
    return this.lookupGoal(this._goal)?.services.find((s) => s.id === ls.id) || undefined;
  };

  private lookupPartner = (lp: Partner | undefined): Partner | undefined => {
    if (lp === undefined) return undefined;
    return this.lookupService(this._serviceType)
      ?.getPartnersBy()
      .find((p) => p.id === lp.id);
  };

  private lookupDate = (ld: RRDate | undefined): RRDate | undefined => {
    if (ld === undefined) return undefined;
    if (this._partner && this._serviceType) {
      return this._partner.canServiceBeProvidedOnDate(ld, this._serviceType) ? ld : undefined;
    }
    if (this._serviceType === undefined) return undefined;
    return this._serviceType.getDates([ld]).length > 0 ? ld : undefined;
  };

  private lookupTime = (lt: Time | undefined): Time | undefined => {
    if (lt === undefined) return undefined;
    return (
      this.lookupPartner(this._partner)
        ?.schedule.timeslotsByDay(this._date!, this._serviceType!.duration)
        .find((t) => t.from.toString("HH:mm") === lt)
        ?.from.toString("HH:mm") || undefined
    );
  };
}
